import React, { useState, useEffect, useRef } from 'react';
import { authService, ApiError } from '../../services';

const RevealPasswordModal = ({ isOpen, onClose, onSuccess }) => {
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [locked, setLocked] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      setPassword('');
      setShowPassword(false);
      setError(null);
      setLocked(false);
      const t = setTimeout(() => inputRef.current?.focus(), 50);
      return () => clearTimeout(t);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = e => {
      if (e.key === 'Escape') onClose?.();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = async e => {
    e.preventDefault();
    if (!password || submitting) return;

    setSubmitting(true);
    setError(null);

    try {
      const result = await authService.revealVerify(password);
      onSuccess?.(result);
      onClose?.();
    } catch (err) {
      const code = err instanceof ApiError ? err.code : null;
      if (code === 'AccountLocked') {
        setLocked(true);
        setError(
          'Account temporarily locked after 3 failed attempts. Please try again in 30 minutes.'
        );
      } else if (code === 'InvalidUsernamePassword') {
        setError('Incorrect password. Please try again.');
      } else {
        setError(
          err?.message || 'Failed to verify password. Please try again.'
        );
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4'
      role='dialog'
      aria-modal='true'
      aria-labelledby='reveal-modal-title'
      onMouseDown={e => {
        if (e.target === e.currentTarget) onClose?.();
      }}
    >
      <div className='w-full max-w-md rounded-lg bg-white shadow-xl'>
        <div className='flex items-start justify-between px-5 py-4 border-b border-gray-200'>
          <div>
            <h2
              id='reveal-modal-title'
              className='text-base font-semibold text-gray-900'
            >
              Confirm Admin Password
            </h2>
            <p className='text-xs text-gray-500 mt-0.5'>
              Enter your password to reveal sensitive information for 5 minutes.
            </p>
          </div>
          <button
            type='button'
            onClick={onClose}
            aria-label='Close'
            className='p-1 text-gray-400 hover:text-gray-600 rounded'
          >
            <svg className='w-5 h-5' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
              <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={2}
                d='M6 18L18 6M6 6l12 12'
              />
            </svg>
          </button>
        </div>

        <form onSubmit={handleSubmit} className='px-5 py-4 space-y-4'>
          <div>
            <label
              htmlFor='reveal-password'
              className='block text-sm font-medium text-gray-700 mb-1'
            >
              Password
            </label>
            <div className='relative'>
              <input
                id='reveal-password'
                ref={inputRef}
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={e => {
                  setPassword(e.target.value);
                  if (error) setError(null);
                }}
                disabled={submitting || locked}
                autoComplete='current-password'
                className='w-full rounded-md border border-gray-300 px-3 py-2 pr-10 text-sm text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500'
                placeholder='Your admin password'
              />
              <button
                type='button'
                onClick={() => setShowPassword(v => !v)}
                tabIndex={-1}
                className='absolute inset-y-0 right-0 flex items-center px-3 text-gray-400 hover:text-gray-600'
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.542-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.542 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21'
                    />
                  </svg>
                ) : (
                  <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M15 12a3 3 0 11-6 0 3 3 0 016 0z'
                    />
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      strokeWidth={2}
                      d='M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z'
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>

          {error && (
            <div
              className={`rounded-md border px-3 py-2 text-sm ${
                locked
                  ? 'border-amber-200 bg-amber-50 text-amber-800'
                  : 'border-red-200 bg-red-50 text-red-700'
              }`}
              role='alert'
            >
              {error}
            </div>
          )}

          <div className='flex items-center justify-end gap-2 pt-1'>
            <button
              type='button'
              onClick={onClose}
              disabled={submitting}
              className='px-4 py-2 rounded-md border border-gray-300 bg-white text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50'
            >
              Cancel
            </button>
            <button
              type='submit'
              disabled={submitting || locked || !password}
              className='px-4 py-2 rounded-md bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50'
            >
              {submitting ? 'Verifying...' : 'Confirm & Reveal'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default RevealPasswordModal;
